import React, { Fragment, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Collapse,
  Grid,
  Typography
} from '@material-ui/core';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  AssignmentLate as AssignmentLateIcon,
  CardGiftcard as CardGiftcardIcon,
  Class as ClassIcon,
  Domain as DomainIcon,
  Error as ErrorIcon,
  LocalLibrary as LocalLibraryIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
  RequestPageOutlined as RequestPageOutlinedIcon
} from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';

import { useUpdateSignup } from '../login/Signup.hooks';
import { BaseOnboardingProps } from '../login/Signup.interface';

interface FeatureItem {
  name: string;
  image?: string;
  description?: string;
  icon?: React.ReactNode;
}

// images located in /public/assets/onboarding
const industries: FeatureItem[] = [
  {
    name: 'Hazard',
    image: 'hazard.png',
    icon: <ReportProblemOutlinedIcon />,
    description:
      'Add Hazards and build traceable design controls to mitigate any potential harm and ISO14971 compliance.'
  },
  {
    name: 'Risk Management',
    image: 'risk-management.png',
    icon: <AssignmentLateIcon />,
    description:
      'Estimate residual risks based on severity and probability from a risk matrix configurable for your product(s).'
  },
  {
    name: 'Documents',
    image: 'documents.png',
    icon: <LocalLibraryIcon />,
    description:
      'Manage documents with intuitive folder structures and views with version controls and smart numbering based on doc types.'
  },
  {
    name: 'Product Management',
    image: 'product-management.png',
    icon: <CardGiftcardIcon />,
    description:
      'Visualize development stages in an intuitive view that can handle multiple approval workflows and generate SDLC deliverables.'
  },
  {
    name: 'Change Order',
    image: 'change-order.png',
    icon: <RequestPageOutlinedIcon />,
    description:
      'Categorize based on change types and perform impact assessments and define quality stage gates for effective change management.'
  },
  {
    name: 'CAPA',
    image: 'capa.png',
    icon: <ErrorIcon />,
    description: `Create CAPA's and track real time progress with status based views for anytime Quality Management Review.`
  },
  {
    name: 'Supplier Management',
    image: 'supplier-management.png',
    icon: <DomainIcon />,
    description:
      'Engage with your suppliers directly from the system for evaluations and SCAR and manage approvals and reminders easily.'
  },
  {
    name: 'Training',
    image: 'training.png',
    icon: <ClassIcon />,
    description:
      'Role based training, version controlled training plans, auto reminders, etc keep training simple and easy to comply.'
  }
];

const StyledDiv = styled.div`
  .max-content {
    max-width: 768px;
    &:not(:last-child) {
      margin-bottom: 24px;
    }
  }
  .header {
    flex: 1;
    .description {
      margin-top: 12px;
    }
  }
`;

const OnboardingQmsFeatures: React.FC<BaseOnboardingProps> = props => {
  const { signup, onContinue, onChange, onUpdate, onError } = props;
  const onChangeRef = useRef(onChange);
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  const [selected, setSelected] = useState(() => signup.features || []);
  const { signup: updatedSignup, error, update } = useUpdateSignup(signup.id);
  const allSelected = selected.length >= industries.length;

  const handleSelect = (name: string) => {
    setSelected(selected => {
      const index = selected.indexOf(name);
      if (index === -1) {
        selected = selected.concat(name);
      } else {
        selected = Array.from(selected);
        selected.splice(index, 1);
      }
      // sort selected values based on provided items
      selected = selected.sort((a, b) => {
        const aIndex = industries.findIndex(item => item.name === a);
        const bIndex = industries.findIndex(item => item.name === b);
        return aIndex - bIndex;
      });
      update({ features: selected });
      return selected;
    });
  };

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    setSelected(signup.features);
  }, [signup.features]);

  useEffect(() => {
    if (updatedSignup) {
      onChangeRef.current?.(updatedSignup);
      onUpdateRef.current?.();
    }
  }, [updatedSignup]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);

  return (
    <StyledDiv>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item className='header'>
              <Typography variant='h2' className='title'>
                QMS Modules
              </Typography>
              <Typography variant='subtitle1' className='description'>
                {selected.length === 0 ? (
                  'Select the QMS modules you would like to use.'
                ) : (
                  <>
                    Selected{' '}
                    <strong className='count-text'>{selected.length}</strong> of{' '}
                    <strong className='count-text'>{industries.length}</strong>{' '}
                    QMS modules
                  </>
                )}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                color='primary'
                variant={allSelected ? 'contained' : 'outlined'}
                endIcon={
                  allSelected ? (
                    <RemoveCircleOutlineIcon />
                  ) : (
                    <AddCircleOutlineIcon />
                  )
                }
                onClick={() => {
                  const features = !allSelected
                    ? industries.map(item => item.name)
                    : [];
                  update({ features });
                  setSelected(features);
                }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </Grid>
          </Grid>
          {/* <Grid item xs={12}>
            <Box mt={3}>
              <OnboardingAlert selected={selected} />
            </Box>
          </Grid> */}
        </Grid>
        {industries.map((item, index) => (
          <Grid key={`${item.name}-${index}`} item xs={12} md={6} lg={4}>
            <FeatureCardItem
              item={item}
              selected={selected.includes(item.name)}
              onClick={() => handleSelect(item.name)}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={8} display='flex' alignItems='center'>
        <Button
          size='large'
          color='primary'
          variant='contained'
          disabled={selected.length === 0}
          onClick={onContinue}
        >
          Continue
        </Button>
        <Box ml={2}>
          {selected.length > 0 && (
            <>
              Selected <strong className='count-text'>{selected.length}</strong>{' '}
              of <strong className='count-text'>{industries.length}</strong> QMS
              modules
            </>
          )}
        </Box>
      </Box>
    </StyledDiv>
  );
};

export default OnboardingQmsFeatures;

const StyledCard = styled(Card)`
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  &.selected {
    border: 2px solid ${props => props.theme.palette.primary.main};
  }
  /* maximize card height via extender */
  .extender {
    flex: 1;
  }
  .title {
    display: flex;
    align-items: center;
    .icon {
      display: flex;
      margin-right: 8px;
      color: ${props => props.theme.palette.text.secondary};
    }
  }
  .media {
    overflow: hidden;
    img {
      width: 100%;
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
    ${props => props.theme.breakpoints.up('md')} {
      max-height: 200px;
      img {
        transform: translateY(-10%);
      }
    }
    ${props => props.theme.breakpoints.down('sm')} {
      max-height: 320px;
      img {
        transform: translateY(-14%);
      }
    }
  }
  .description {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
`;

interface FeatureCardItemProps {
  item: FeatureItem;
  selected?: boolean;
  onClick?: () => void;
}

const FeatureCardItem: React.FC<FeatureCardItemProps> = props => {
  const { item, selected, onClick } = props;
  const src = item.image && `/assets/onboarding/${item.image}`;
  return (
    <StyledCard elevation={1} className={selected ? 'selected' : undefined}>
      {src && (
        <CardMedia className='media'>
          <img src={src} alt={item.name} draggable='false' />
        </CardMedia>
      )}
      <CardContent>
        <Box className='title' mb={2}>
          {item.icon && <span className='icon'>{item.icon}</span>}
          <Typography variant='h6'>{item.name}</Typography>
        </Box>
        <Typography
          variant='body2'
          className='description'
          color={!item.description ? 'textSecondary' : undefined}
        >
          {item.description || 'No description provided.'}
        </Typography>
      </CardContent>
      <div className='extender' />
      <CardActions>
        <Button
          fullWidth
          color='primary'
          endIcon={
            selected ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />
          }
          variant={selected ? 'contained' : 'outlined'}
          onClick={onClick}
        >
          {selected ? 'Remove' : 'Add'} Feature
        </Button>
      </CardActions>
    </StyledCard>
  );
};

// const StyledAlert = styled(Alert)`
//   .features {
//     margin-top: 8px;
//   }
//   .number-text {
//     font-size: 18px;
//   }
//   .MuiAlertTitle-root {
//     margin-top: -4px;
//   }
//   &.info .count-text {
//     color: ${props => props.theme.palette.info.dark};
//   }
//   &.success .count-text {
//     color: ${props => props.theme.palette.success.dark};
//   }
// `;

// interface SelectedAlertProps {
//   selected: string[];
// }

// const OnboardingAlert: React.FC<SelectedAlertProps> = props => {
//   const { selected } = props;
//   const { length } = selected;
//   const severity = length > 0 ? 'success' : 'info';
//   const classes = ['onboarding-alert', severity];
//   return (
//     <StyledAlert className={classes.join(' ')} severity={severity}>
//       <AlertTitle>
//         You have <strong className='count-text'>{length}</strong> of{' '}
//         <strong className='count-text'>{industries.length}</strong> QMS modules
//         selected
//       </AlertTitle>
//       <Typography>
//         Selecting features provided will help us assess what best meets your
//         needs.
//       </Typography>
//       <Collapse in={length > 0}>
//         <Typography className='features'>
//           Features included:{' '}
//           {selected.map((item, index) => (
//             <Fragment key={item}>
//               <strong>{item}</strong>
//               {index < length - 1 && ', '}
//             </Fragment>
//           ))}
//         </Typography>
//       </Collapse>
//     </StyledAlert>
//   );
// };
